import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, CalendarDays, ChevronDown, ChevronRight, Download, Lock, LockOpen, CheckCircle2, Send } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha, formatFechaHora, hoyCaracas } from '../utils/format';
import { descargarArchivo } from '../components/reportes/Exportar';
import BotonAyuda from '../components/BotonAyuda';
import Modal from '../components/Modal';
import Retenciones from './Retenciones';
import LibroCompras from './LibroCompras';
import LibroVentas from './LibroVentas';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// ============================================================
// Impuestos (#152, maqueta aprobada por Armando 2026-10-14). Arriba, el
// calendario de lo que vence en el mes: cada declaración de la empresa
// activa con su monto calculado, vencimiento y estado; al abrir una fila
// se cierra la quincena del IVA retenido, se marca declarada o pagada y se
// bajan los archivos para el portal del SENIAT. Debajo, pestañas de
// detalle: resumen del mes que se declara, Retenciones y los dos libros
// (las mismas páginas de antes, embebidas). Las reglas viven en el
// servidor (impuestosController).
// ============================================================

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const nombreMes = (mes) => `${MESES[Number(mes.slice(5, 7)) - 1]} ${mes.slice(0, 4)}`;
const finDeMes = (mes) => {
  const [a, m] = mes.split('-').map(Number);
  return `${mes}-${String(new Date(a, m, 0).getDate()).padStart(2, '0')}`;
};

const diaSiguiente = (f) => {
  const d = new Date(`${f}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

const ESTADOS = {
  pagada: { texto: 'Pagada', clase: 'bg-green-50 text-green-700' },
  declarada: { texto: 'Declarada', clase: 'bg-primary-50 text-primary-700' },
  por_declarar: { texto: 'Por declarar', clase: 'bg-amber-50 text-amber-700' },
  por_cerrar: { texto: 'Por cerrar', clase: 'bg-amber-50 text-amber-700' },
  abierta: { texto: 'Quincena abierta', clase: 'bg-slate-100 text-slate-600' },
  en_curso: { texto: 'En curso', clase: 'bg-slate-100 text-slate-600' }
};
const OBLIGACIONES_CAL = {
  iva_retenido_q1: 'IVA ret. 1ª q.', iva_retenido_q2: 'IVA ret. 2ª q.', iva: 'IVA del mes', islr_retenido: 'ISLR ret.',
  municipal_retenido: 'Municipal', igtf_q1: 'IGTF 1ª q.', igtf_q2: 'IGTF 2ª q.'
};
const PESTANAS = [
  { key: 'resumen', label: 'Resumen' },
  { key: 'retenciones', label: 'Retenciones', ruta: '/reportes/retenciones' },
  { key: 'compras', label: 'Libro de compras', ruta: '/reportes/libro-compras' },
  { key: 'ventas', label: 'Libro de ventas', ruta: '/reportes/libro-ventas' }
];

const btn = 'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed';
const btnPrimario = `${btn} bg-primary-600 text-white hover:bg-primary-700`;
const btnSecundario = `${btn} border border-slate-300 text-slate-700 hover:bg-slate-50`;

function Chip({ o }) {
  const e = ESTADOS[o.estado];
  const soloVencida = o.vencida && ['por_declarar', 'por_cerrar'].includes(o.estado);
  return (
    <span className="inline-flex flex-wrap gap-1">
      {o.vencida && <span className="text-xs px-2 py-1 rounded-full whitespace-nowrap bg-red-50 text-red-700">Vencida</span>}
      {!soloVencida && <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${e.clase}`}>{e.texto}</span>}
    </span>
  );
}

const nombreObligacion = (o) => `${o.label}${o.q ? ` ${o.q === 'Q1' ? '1ª' : '2ª'} quincena` : ''} · ${o.periodo_texto}`;

function Linea({ texto, valor, total }) {
  return (
    <div className={`flex justify-between gap-3 text-sm tabular-nums ${total ? 'border-t border-slate-200 pt-1.5 font-semibold text-slate-800' : ''}`}>
      <span className={total ? '' : 'text-slate-500'}>{texto}</span>
      <span>{valor}</span>
    </div>
  );
}

function CalculoIva({ d }) {
  return (
    <div className="space-y-1">
      <Linea texto="Débito fiscal (Libro de ventas)" valor={formatMonto(d.debito)} />
      <Linea texto="− Crédito fiscal (Libro de compras)" valor={formatMonto(d.credito)} />
      {d.excedente_anterior > 0 && <Linea texto="− Excedente del mes anterior" valor={formatMonto(d.excedente_anterior)} />}
      <Linea texto="− IVA que nos retuvieron los clientes" valor={formatMonto(d.retenido_clientes)} />
      <Linea texto="IVA a pagar" valor={formatMonto(d.a_pagar)} total />
      {d.excedente_siguiente > 0 && <p className="text-xs text-slate-500">Queda un excedente de Bs {formatMonto(d.excedente_siguiente)} para el mes siguiente.</p>}
    </div>
  );
}

// Elegir el egreso con que se pagó (o marcar pagada sin movimiento).
function ModalPago({ o, onClose, onListo }) {
  const [datos, setDatos] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/impuestos/${o.tipo}/${o.periodo}/candidatos`).then((r) => setDatos(r.data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudieron cargar los movimientos'));
  }, [o.tipo, o.periodo]);

  async function pagar(movimientoId) {
    setOcupado(true);
    setError('');
    try {
      await api.post(`/impuestos/${o.tipo}/${o.periodo}/pagar`, { movimiento_id: movimientoId || null });
      onListo();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo marcar pagada');
      setOcupado(false);
    }
  }

  return (
    <Modal title={`Pago de ${nombreObligacion(o)}`} onClose={onClose} maxWidth="max-w-2xl">
      <p className="text-sm text-slate-600 mb-3">Elige el egreso en Bs con que se pagó (Bs {formatMonto(datos?.monto ?? o.monto)}). Los más parecidos al monto van primero.</p>
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
      {!datos ? <p className="text-slate-500 text-sm">Cargando...</p> : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr><th className="text-left p-2">Fecha</th><th className="text-left p-2">Cuenta</th><th className="text-left p-2">Descripción</th><th className="text-right p-2">Monto</th><th className="p-2" /></tr>
            </thead>
            <tbody className="divide-y">
              {datos.movimientos.length === 0 ? (
                <tr><td colSpan={5} className="p-4 text-center text-slate-400">No hay egresos en Bs sin usar entre el inicio del período y 45 días después del vencimiento.</td></tr>
              ) : datos.movimientos.map((m) => (
                <tr key={m.id}>
                  <td className="p-2 whitespace-nowrap">{formatFecha(m.fecha)}</td>
                  <td className="p-2">{m.cuenta}</td>
                  <td className="p-2 text-slate-600">{m.descripcion || m.categoria || '—'}{m.referencia ? ` · ${m.referencia}` : ''}</td>
                  <td className={`p-2 text-right tabular-nums ${Math.abs(m.monto - datos.monto) <= 0.01 ? 'font-semibold text-green-700' : ''}`}>{formatMonto(m.monto)}</td>
                  <td className="p-2 text-right"><button type="button" className={btnSecundario} disabled={ocupado} onClick={() => pagar(m.id)}>Es este</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <button type="button" className={btnSecundario} disabled={ocupado} onClick={() => pagar(null)}>Marcar pagada sin movimiento</button>
      </div>
    </Modal>
  );
}

// Acciones y datos de una obligación (fila abierta del calendario).
function DetalleObligacion({ o, esContador, esGerente, onCambio, onVer }) {
  const [editando, setEditando] = useState(null); // 'declarar' | 'reabrir'
  const [texto, setTexto] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [pagando, setPagando] = useState(false);
  const r = o.registro;
  const base = `/impuestos/${o.tipo}/${o.periodo}`;

  async function accion(fn) {
    setOcupado(true);
    setError('');
    try {
      await fn();
      setEditando(null);
      setTexto('');
      await onCambio();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo completar la acción');
    } finally {
      setOcupado(false);
    }
  }

  async function bajar(tipoArchivo) {
    try {
      const { data } = await api.get(`${base}/${tipoArchivo}`, { responseType: 'blob' });
      descargarArchivo(data, tipoArchivo === 'txt' ? `retenciones-iva-${o.periodo}.txt` : `retenciones-islr-${o.periodo}.xml`);
    } catch {
      setError('No se pudo generar el archivo');
    }
  }

  function cerrarQuincena() {
    if (!window.confirm(`¿Cerrar la ${o.q === 'Q1' ? '1ª' : '2ª'} quincena (${o.periodo_texto})? Nadie podrá cargar, editar ni anular retenciones de IVA con fecha en ella. Solo un gerente o admin la reabre.`)) return;
    accion(() => api.post(`${base}/cerrar`));
  }

  const puedeCerrar = o.tipo === 'iva_retenido' && o.terminado && (!r || r.estado === 'abierta');
  const puedeDeclarar = o.terminado && !['declarada', 'pagada'].includes(r?.estado);
  const puedePagar = o.terminado && r?.estado !== 'pagada';
  const puedeReabrir = r && r.estado !== 'abierta';

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="space-y-2">
        {o.tipo === 'iva' && o.detalle ? <CalculoIva d={o.detalle} /> : (
          <p className="text-sm text-slate-600">
            {o.tipo === 'igtf' ? 'IGTF percibido en las facturas de venta fiscales y los cierres Z del período.'
              : `${o.cantidad} comprobante${o.cantidad === 1 ? '' : 's'} de retención en el período.`}
            {!o.terminado && ' El monto se actualiza solo hasta que termine el período.'}
          </p>
        )}
        {o.sin_tasa > 0 && (
          <p className="flex gap-1.5 text-sm text-amber-700"><AlertTriangle size={15} className="mt-0.5 shrink-0" />{o.sin_tasa} factura{o.sin_tasa === 1 ? '' : 's'} en dólares sin tasa BCV: no suman al monto.</p>
        )}
        {o.tipo === 'iva' && o.detalle?.sin_control > 0 && (
          <p className="flex gap-1.5 text-sm text-amber-700"><AlertTriangle size={15} className="mt-0.5 shrink-0" />{o.detalle.sin_control} factura{o.detalle.sin_control === 1 ? '' : 's'} fiscal{o.detalle.sin_control === 1 ? '' : 'es'} sin número de control.</p>
        )}
        {o.difiere && (
          <p className="flex gap-1.5 text-sm text-amber-700"><AlertTriangle size={15} className="mt-0.5 shrink-0" />Quedó en Bs {formatMonto(o.monto)}, pero hoy el cálculo da Bs {formatMonto(o.monto_calculado)}: cambió algo después de cerrar o declarar.</p>
        )}
        {(o.tipo === 'iva_retenido' || o.tipo === 'islr_retenido' || o.tipo === 'municipal_retenido') && (
          <button type="button" className="text-sm text-primary-600 hover:text-primary-700" onClick={() => onVer('retenciones')}>Ver comprobantes</button>
        )}
        {o.tipo === 'iva' && (
          <button type="button" className="text-sm text-primary-600 hover:text-primary-700" onClick={() => onVer('compras')}>Ver libros</button>
        )}
      </div>

      <div className="space-y-2 text-sm">
        {r?.cerrada_en && <p className="text-slate-600">Quincena cerrada el {formatFechaHora(r.cerrada_en)} por {r.cerrada_por}.</p>}
        {r?.declarada_en && <p className="text-slate-600">Declarada el {formatFechaHora(r.declarada_en)} por {r.declarada_por}{r.numero_planilla ? `. Planilla Nº ${r.numero_planilla}` : ''}.</p>}
        {r?.estado === 'pagada' && (
          <p className="text-slate-600">
            {r.movimiento
              ? `Pagada con el egreso del ${formatFecha(r.movimiento.fecha)} en ${r.movimiento.cuenta} por Bs ${formatMonto(r.movimiento.monto)}${r.movimiento.descripcion ? ` (${r.movimiento.descripcion})` : ''}.`
              : 'Pagada sin movimiento asociado.'}
            {r.pagada_sola ? ' Coincidió el monto exacto, así que pasó a Pagada sola.' : r.pagada_por ? ` La marcó ${r.pagada_por}.` : ''}
          </p>
        )}
        {r?.estado === 'abierta' && r.reabierta_en && <p className="text-slate-600">Reabierta el {formatFechaHora(r.reabierta_en)} por {r.reabierta_por}: {r.motivo_reapertura}</p>}
        {!o.terminado && o.tipo === 'iva_retenido' && <p className="text-slate-500">La quincena se cierra desde el {formatFecha(diaSiguiente(o.hasta))}.</p>}
        {o.terminado && !r && o.monto > 0 && <p className="text-slate-500">Pasa a Pagada sola cuando aparezca un egreso en Bs por el monto exacto.</p>}

        {error && <p className="text-red-600">{error}</p>}

        {esContador && (
          <div className="flex flex-wrap gap-2 pt-1">
            {puedeCerrar && <button type="button" className={btnPrimario} disabled={ocupado} onClick={cerrarQuincena}><Lock size={14} />Cerrar quincena</button>}
            {puedeDeclarar && editando !== 'declarar' && <button type="button" className={puedeCerrar ? btnSecundario : btnPrimario} disabled={ocupado} onClick={() => { setEditando('declarar'); setTexto(''); }}><Send size={14} />Marcar declarada</button>}
            {puedePagar && <button type="button" className={btnSecundario} disabled={ocupado} onClick={() => setPagando(true)}><CheckCircle2 size={14} />Marcar pagada</button>}
            {o.tipo === 'iva_retenido' && o.cantidad > 0 && <button type="button" className={btnSecundario} onClick={() => bajar('txt')}><Download size={14} />TXT para el portal</button>}
            {o.tipo === 'islr_retenido' && o.cantidad > 0 && <button type="button" className={btnSecundario} onClick={() => bajar('xml')}><Download size={14} />XML para el portal</button>}
            {esGerente && puedeReabrir && editando !== 'reabrir' && <button type="button" className={btnSecundario} disabled={ocupado} onClick={() => { setEditando('reabrir'); setTexto(''); }}><LockOpen size={14} />Reabrir</button>}
          </div>
        )}

        {editando && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <input
              autoFocus
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder={editando === 'declarar' ? 'Nº de planilla (opcional)' : 'Motivo para reabrir'}
              className="flex-1 rounded-md border px-3 py-1.5 text-sm"
            />
            <div className="flex gap-2">
              <button
                type="button"
                className={btnPrimario}
                disabled={ocupado || (editando === 'reabrir' && !texto.trim())}
                onClick={() => accion(() => (editando === 'declarar'
                  ? api.post(`${base}/declarar`, { numero_planilla: texto.trim() })
                  : api.post(`${base}/reabrir`, { motivo: texto.trim() })))}
              >
                {editando === 'declarar' ? 'Declarada' : 'Reabrir'}
              </button>
              <button type="button" className={btnSecundario} onClick={() => setEditando(null)}>Cancelar</button>
            </div>
          </div>
        )}
      </div>

      {pagando && <ModalPago o={o} onClose={() => setPagando(false)} onListo={async () => { setPagando(false); await onCambio(); }} />}
    </div>
  );
}

// Calendario SENIAT del año para el último dígito del RIF de la empresa.
function ModalCalendario({ esGerente, onClose, onGuardado }) {
  const [anio, setAnio] = useState(Number(hoyCaracas().slice(0, 4)));
  const [datos, setDatos] = useState(null);
  const [fechas, setFechas] = useState({});
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setDatos(null);
    api.get('/impuestos/calendario', { params: { anio } }).then((r) => {
      setDatos(r.data);
      const f = {};
      for (const fila of r.data.filas) for (const ob of r.data.obligaciones) f[`${fila.mes}|${ob}`] = fila.fechas[ob].fecha || '';
      setFechas(f);
    }).catch((err) => setError(err.response?.data?.error || 'No se pudo cargar el calendario'));
  }, [anio]);

  async function guardar() {
    setOcupado(true);
    setError('');
    try {
      const lista = Object.entries(fechas).map(([k, fecha]) => {
        const [mes, obligacion] = k.split('|');
        return { mes: Number(mes), obligacion, fecha: fecha || null };
      });
      await api.put('/impuestos/calendario', { anio, fechas: lista });
      onGuardado();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar');
      setOcupado(false);
    }
  }

  return (
    <Modal title="Calendario SENIAT" onClose={onClose} maxWidth="max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-sm text-slate-600">
          {datos?.digito != null
            ? <>Fechas de vencimiento de contribuyentes especiales con RIF terminado en <b>{datos.digito}</b>. Cada fila es el mes del período que se declara. Vacío = se usa una fecha estimada.</>
            : 'La empresa no tiene RIF cargado: sin él no se sabe qué fechas le tocan.'}
        </p>
        <select value={anio} onChange={(e) => setAnio(Number(e.target.value))} className="border rounded-md px-2 py-1.5 text-sm">
          {[anio - 1, anio, anio + 1].map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
      {!datos ? <p className="text-sm text-slate-500">Cargando...</p> : datos.digito != null && (
        <div className="overflow-x-auto border rounded-lg">
          <table className="text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="text-left p-2">Período</th>
                {datos.obligaciones.map((ob) => <th key={ob} className="text-left p-2 whitespace-nowrap">{OBLIGACIONES_CAL[ob]}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y">
              {datos.filas.map((fila) => (
                <tr key={fila.mes}>
                  <td className="p-2 whitespace-nowrap capitalize">{MESES[fila.mes - 1]}</td>
                  {datos.obligaciones.map((ob) => {
                    const k = `${fila.mes}|${ob}`;
                    return (
                      <td key={ob} className="p-1">
                        <input
                          type="date"
                          disabled={!esGerente}
                          value={fechas[k] || ''}
                          title={`Estimada: ${formatFecha(fila.fechas[ob].estimada)}`}
                          onChange={(e) => setFechas((f) => ({ ...f, [k]: e.target.value }))}
                          className={`border rounded px-1.5 py-1 text-xs ${fechas[k] ? '' : 'text-slate-400'}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className={btnSecundario} onClick={onClose}>{esGerente ? 'Cancelar' : 'Cerrar'}</button>
        {esGerente && datos?.digito != null && <button type="button" className={btnPrimario} disabled={ocupado} onClick={guardar}>Guardar</button>}
      </div>
    </Modal>
  );
}

function ResumenPeriodo({ resumen, mes }) {
  const grupos = ['iva', 'iva_retenido', 'islr_retenido', 'municipal_retenido', 'igtf']
    .map((tipo) => resumen.filter((o) => o.tipo === tipo))
    .filter((g) => g.length);
  if (!grupos.length) return <p className="text-sm text-slate-500">Sin obligaciones para {nombreMes(mes)}.</p>;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {grupos.map((g) => (
        <div key={g[0].tipo} className="rounded-lg border border-slate-200 p-3 space-y-2">
          <h3 className="text-sm font-semibold text-slate-700">{g[0].label}</h3>
          {g[0].tipo === 'iva' && g[0].detalle ? (
            <>
              <CalculoIva d={g[0].detalle} />
              <Chip o={g[0]} />
            </>
          ) : (
            <>
              {g.map((o) => (
                <div key={o.periodo} className="flex items-center justify-between gap-2 text-sm tabular-nums">
                  <span className="text-slate-500">{o.q ? `${o.q === 'Q1' ? '1ª' : '2ª'} quincena` : 'Mes'}</span>
                  <span className="flex items-center gap-2">{formatMonto(o.monto)} <Chip o={o} /></span>
                </div>
              ))}
              {g.length > 1 && <Linea texto="Total" valor={formatMonto(g.reduce((s, o) => s + Number(o.monto || 0), 0))} total />}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Impuestos() {
  const { empresaActiva, empresaActivaNombre, rolActual } = useAuth();
  const { pathname } = useLocation();
  const [mes, setMes] = useState(hoyCaracas().slice(0, 7));
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [abierta, setAbierta] = useState(null); // `${tipo}|${periodo}`
  const [pestana, setPestana] = useState(() => PESTANAS.find((p) => p.ruta === pathname)?.key || 'resumen');
  const [calendario, setCalendario] = useState(false);

  const esContador = ['contador', 'gerente', 'admin'].includes(rolActual);
  const esGerente = ['gerente', 'admin'].includes(rolActual);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/impuestos', { params: { mes } });
      setDatos(r.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar Impuestos');
    }
  }, [mes]);

  useEffect(() => { if (empresaActiva) cargar(); }, [empresaActiva, cargar]);

  function verPestana(key) {
    setPestana(key);
    document.getElementById('impuestos-pestanas')?.scrollIntoView({ behavior: 'smooth' });
  }

  const cal = datos?.calendario || [];
  const sinPagar = cal.filter((o) => o.estado !== 'pagada');
  const porPagar = sinPagar.reduce((s, o) => s + Number(o.monto || 0), 0);
  const hoy = datos?.hoy || hoyCaracas();
  const proximo = sinPagar.filter((o) => o.vence >= hoy && o.monto > 0).sort((a, b) => a.vence.localeCompare(b.vence))[0];
  const vencidas = cal.filter((o) => o.vencida);
  const diasPara = (f) => Math.round((new Date(`${f}T00:00:00`) - new Date(`${hoy}T00:00:00`)) / 86400000);
  const mesResumen = datos?.mes_resumen;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            Impuestos{empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
            <BotonAyuda ruta="/impuestos" />
          </h1>
          <p className="text-sm text-slate-500">Declaraciones que vencen en el mes, con su monto, vencimiento y estado.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm" aria-label="Mes" />
          <button type="button" className={btnSecundario} onClick={() => setCalendario(true)}><CalendarDays size={15} />Calendario SENIAT</button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {datos && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl shadow-sm p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Por pagar en {MESES[Number(mes.slice(5, 7)) - 1]}</p>
              <p className="text-xl font-semibold text-slate-800 tabular-nums">Bs {formatMonto(porPagar)}</p>
              <p className="text-xs text-slate-400">{sinPagar.length} obligaci{sinPagar.length === 1 ? 'ón' : 'ones'} sin pagar</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Próximo vencimiento</p>
              <p className="text-xl font-semibold text-amber-700 tabular-nums">{proximo ? formatFecha(proximo.vence).slice(0, 5) : '—'}</p>
              <p className="text-xs text-slate-400">{proximo ? `${proximo.label} · ${diasPara(proximo.vence) === 0 ? 'hoy' : `en ${diasPara(proximo.vence)} día${diasPara(proximo.vence) === 1 ? '' : 's'}`}` : 'Nada por vencer'}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Vencidas</p>
              <p className={`text-xl font-semibold tabular-nums ${vencidas.length ? 'text-red-700' : 'text-slate-800'}`}>{vencidas.length}</p>
              <p className="text-xs text-slate-400 truncate">{vencidas.length ? vencidas.map((o) => o.label).join(', ') : 'Ninguna'}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Pagadas</p>
              <p className="text-xl font-semibold text-slate-800 tabular-nums">{cal.length - sinPagar.length} de {cal.length}</p>
              <p className="text-xs text-slate-400">Detectadas en movimientos o marcadas</p>
            </div>
          </div>

          <section className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
              <h2 className="text-base font-semibold text-slate-700">Calendario de {nombreMes(mes)}</h2>
              {datos.calendario_estimado && (
                <span className="text-xs text-slate-500">
                  * Fecha estimada: {esGerente ? 'carga' : 'falta cargar'} el calendario SENIAT {mes.slice(0, 4)}{datos.empresa.digito != null ? ` del RIF terminado en ${datos.empresa.digito}` : ' (la empresa no tiene RIF)'}.
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs">
                  <tr>
                    <th className="text-left p-2 pl-4">Vence</th>
                    <th className="text-left p-2">Obligación</th>
                    <th className="text-left p-2 hidden sm:table-cell">Período</th>
                    <th className="text-right p-2">Monto Bs</th>
                    <th className="text-left p-2">Estado</th>
                    <th className="p-2 hidden sm:table-cell" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {cal.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">Nada vence en {nombreMes(mes)}.</td></tr>}
                  {cal.map((o) => {
                    const k = `${o.tipo}|${o.periodo}`;
                    const abiertaFila = abierta === k;
                    return (
                      <React.Fragment key={k}>
                        <tr {...filaClickeable(() => setAbierta(abiertaFila ? null : k))} className={`cursor-pointer transition-colors ${abiertaFila ? 'bg-primary-50' : 'hover:bg-slate-50'} ${FOCO_FILA}`}>
                          <td className="p-2 pl-4 whitespace-nowrap tabular-nums">{formatFecha(o.vence).slice(0, 5)}{o.vence_estimado && <span className="text-slate-400" title="Fecha estimada">*</span>}</td>
                          <td className="p-2">
                            <span className="font-medium text-slate-800">{o.label}</span>
                            <span className="block text-xs text-slate-500">{o.q ? `${o.q === 'Q1' ? '1ª' : '2ª'} quincena` : o.tipo === 'municipal_retenido' ? 'Mensual · Alcaldía' : 'Mensual'}<span className="sm:hidden"> · {o.periodo_texto}</span></span>
                          </td>
                          <td className="p-2 whitespace-nowrap hidden sm:table-cell">{o.periodo_texto}</td>
                          <td className="p-2 text-right tabular-nums whitespace-nowrap">{formatMonto(o.monto)}</td>
                          <td className="p-2"><Chip o={o} /></td>
                          <td className="p-2 pr-4 text-slate-400 hidden sm:table-cell">{abiertaFila ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                        </tr>
                        {abiertaFila && (
                          <tr className="bg-slate-50">
                            <td colSpan={6} className="p-4 max-w-[100vw]">
                              <DetalleObligacion o={o} esContador={esContador} esGerente={esGerente} onCambio={cargar} onVer={verPestana} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section id="impuestos-pestanas" className="bg-white rounded-xl shadow-sm">
            <div className="flex gap-1 border-b overflow-x-auto px-2 print:hidden">
              {PESTANAS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPestana(p.key)}
                  className={`px-3.5 py-2.5 text-sm whitespace-nowrap ${pestana === p.key ? 'font-semibold text-primary-600 border-b-2 border-primary-600' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {p.key === 'resumen' ? `Resumen de ${MESES[Number(mesResumen.slice(5, 7)) - 1]}` : p.label}
                </button>
              ))}
            </div>
            <div className="p-4">
              {pestana === 'resumen' && <ResumenPeriodo resumen={datos.resumen} mes={mesResumen} />}
              {pestana === 'retenciones' && <Retenciones key={mesResumen} embebido desdeInicial={`${mesResumen}-01`} hastaInicial={finDeMes(mesResumen)} />}
              {pestana === 'compras' && <LibroCompras key={mesResumen} embebido desdeInicial={`${mesResumen}-01`} hastaInicial={finDeMes(mesResumen)} />}
              {pestana === 'ventas' && <LibroVentas key={mesResumen} embebido desdeInicial={`${mesResumen}-01`} hastaInicial={finDeMes(mesResumen)} />}
            </div>
          </section>
        </>
      )}

      {calendario && <ModalCalendario esGerente={esGerente} onClose={() => setCalendario(false)} onGuardado={() => { setCalendario(false); cargar(); }} />}
    </div>
  );
}
